import { useState, useEffect } from "react";
import { Tabs, Tooltip, notification, Button } from "antd";
import { useNavigate } from "react-router";
import StickyBox from "react-sticky-box";
import GuideButton from "@/components/UI/GuideButton";
import InfoTooltip from "@/components/UI/InfoTooltip";
import DisplayModeToggle from "@/components/UI/DisplayModeToggle";
import SegmentedToggle from "@/components/UI/SegmentedToggle";
import { useInsightsQuery } from "@/hooks/useInsightsQuery";
import InsightInformationDrawer from "@/components/Insights/InsightInformationDrawer";
import { InsightProps } from "@/components/Insights/insights";
import InsightModal from "@/components/Insights/InsightModal";
import DeleteInsightModal from "@/components/Insights/DeleteInsightModal";
import AddAlertModal from "@/components/Insights/AddAlertModal";
import ShareInsightModal from "@/components/Insights/ShareInsightModal";
import InsightsPanelLayout from "@/components/Insights/InsightsPanelLayout";
import {
  buildInsightSharingRequest,
  type InsightEditRequest,
} from "@/components/Insights/insightsActions";
import { useInsightMutations } from "@/hooks/useInsightMutations";
import { useScopeParams } from "@/hooks/useScopeParams";
import { filterInsights } from "./insights.utils";
import { getInsightSuccessCopy } from "./insightNotifications";
import AddIcon from "@/assets/icons/add.svg?react";

const categories = [
  { label: "All", key: "all" },
  { label: "Uncategorized", key: "uncategorized" },
  { label: "Sites", key: "sites" },
  { label: "Subjects", key: "subjects" },
  { label: "Safety", key: "safety" },
  { label: "Clinical", key: "clinical" },
  { label: "Data", key: "data" },
];

type ActiveModal = "edit" | "delete" | "alert" | "share" | null;

export default function Insights() {
  // Insights that carry an alert are also listed on the Alerts page, but they
  // stay listed here as well: filtering them out emptied this page — disabling
  // every tab and toggle — as soon as a user's only insight had an alert.
  const { insights, isLoading } = useInsightsQuery();

  const [currentInsight, setCurrentInsight] = useState<InsightProps | null>(
    null,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [isActive, setIsActive] = useState(true);
  const [compactMode, setCompactMode] = useState(false);
  const [messageApi, contextHolder] = notification.useNotification();
  const [deleteMode, setDeleteMode] = useState<"insight" | "alert">("insight");

  const [activeTab, setActiveTab] = useState("all");

  const archivedInsights = filterInsights(
    insights,
    "all",
    false,
  ) as InsightProps[];
  const activeInsights = filterInsights(
    insights,
    "all",
    true,
  ) as InsightProps[];
  const categorizedInsights = categories
    .filter((category) => category.key !== "all")
    .reduce(
      (acc, category) => {
        acc[category.key] = {
          active: filterInsights(
            insights,
            category.key,
            true,
          ) as InsightProps[],
          archived: filterInsights(
            insights,
            category.key,
            false,
          ) as InsightProps[],
        };
        return acc;
      },
      {} as Record<
        string,
        { active: InsightProps[]; archived: InsightProps[] }
      >,
    );

  const getInsightsForCategory = (categoryKey: string) =>
    categoryKey === "all"
      ? isActive
        ? activeInsights
        : archivedInsights
      : isActive
        ? categorizedInsights[categoryKey].active
        : categorizedInsights[categoryKey].archived;

  const getMineInsightsForCategory = (categoryKey: string) =>
    getInsightsForCategory(categoryKey).filter(
      (insight: any) => !insight.is_shared,
    );

  const getSharedInsightsForCategory = (categoryKey: string) =>
    getInsightsForCategory(categoryKey).filter(
      (insight: any) => insight.is_shared,
    );

  const openSuccessNotification = (action: string) => {
    const { verb, message } = getInsightSuccessCopy(action);

    messageApi.open({
      title: (
        <p className="text-[#003832] text-base font-semibold">
          Insight {verb}!
        </p>
      ),
      description: (
        <p className="text-[#2B4752] text-[14px]">
          You have successfully {verb} {message}
        </p>
      ),
      placement: "bottom",
      className: "insight-success-confirmation",
      duration: 3,
      icon: null,
    });
  };

  const openErrorNotification = (action: string) => {
    const isAlertAction = action.includes("alert");
    messageApi.open({
      title: (
        <p className="text-[#73134A] text-base font-semibold">
          Something went wrong.
        </p>
      ),
      description: (
        <p className="text-[#260618] text-[14px]">
          There was an error {action}{" "}
          {isAlertAction ? "the alert" : "the insight"}. Please reload the page
          and try again.
        </p>
      ),
      placement: "bottom",
      className: "insight-error-confirmation",
      duration: 3,
      icon: null,
    });
  };

  const {
    handleEditInsight,
    handleDelete,
    handleActivateInsight,
    handleArchiveInsight,
    confirmSubmitLoading,
    confirmDeleteLoading,
  } = useInsightMutations({
    openSuccessNotification,
    openErrorNotification,
  });

  const handleEditInsightWithModal = (insightId: string, payload: any) => {
    handleEditInsight(
      { insightId, payload },
      {
        onSuccess: () => {
          setActiveModal(null);
        },
        onError: (error) => {
          console.error("Error editing insight:", error);
        },
      },
    );
  };

  const handleDeleteWithModal = (
    _insight: any,
    id: string,
    type: "insight" | "alert" = "insight",
  ) => {
    handleDelete(
      { insightId: id, type },
      {
        onSuccess: () => {
          setActiveModal(null);
        },
        onError: (error) => {
          console.error("Error deleting:", error);
        },
      },
    );
  };

  const handleOpenDrawer = (insight: InsightProps) => {
    setCurrentInsight(insight);
    setIsDrawerOpen(true);
  };

  const handleOpenDeleteModal = (
    insight: InsightProps,
    mode: "insight" | "alert" = "insight",
  ) => {
    setCurrentInsight(insight);
    setDeleteMode(mode);
    setActiveModal("delete");
  };

  const handleOpenDeleteAlertModal = (insight: InsightProps) => {
    handleOpenDeleteModal(insight, "alert");
  };

  const handleOpenEditModal = (insight: InsightProps) => {
    setCurrentInsight(insight);
    setActiveModal("edit");
  };

  const handleOpenAlertModal = (insight: InsightProps) => {
    setCurrentInsight(insight);
    setActiveModal("alert");
  };

  const handleOpenShareModal = (insight: InsightProps) => {
    setCurrentInsight(insight);
    setActiveModal("share");
  };

  useEffect(() => {
    if (!isDrawerOpen || !currentInsight) return;

    const updatedInsight = insights.find(
      (insight: any) => insight.id === currentInsight.id,
    );

    if (updatedInsight && updatedInsight !== currentInsight) {
      setCurrentInsight(updatedInsight as InsightProps);
    }
  }, [currentInsight, insights, isDrawerOpen]);

  const handleShareMutation = (request: InsightEditRequest) => {
    handleEditInsight(request, {
      onSuccess: () => {
        setActiveModal(null);
      },
      onError: (error) => {
        console.error(
          `Error ${request.action === "sharing" ? "sharing" : "stopping sharing"} insight:`,
          error,
        );
      },
    });
  };

  const handleShareInsight = (insightId: string) => {
    handleShareMutation(buildInsightSharingRequest(insightId, true));
  };

  const handleStopSharing = (insightId: string) => {
    handleShareMutation(buildInsightSharingRequest(insightId, false));
  };

  //Added to avoid scroll up background cards to top when modal is open
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (isDrawerOpen || activeModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen, activeModal]);

  const isInitialState = insights.length === 0 && !isLoading;

  const renderTabBar = (props: any, DefaultTabBar: any) => (
    <StickyBox offsetTop={64} offsetBottom={20} style={{ zIndex: 1 }}>
      <DefaultTabBar {...props} style={{ background: "#eff1f2" }} />
    </StickyBox>
  );

  const statusOptions = [
    {
      label: (
        <Tooltip
          title="Insights in this view are active and scheduled to be automatically re-run by the Vivo at your set cadence. Results are continuously updated based on the latest available trial data."
          mouseEnterDelay={1}
        >
          <span data-testid="insights-active-status-option">Active</span>
        </Tooltip>
      ),
      value: "Active",
    },
    {
      label: (
        <Tooltip
          title="Insights in this view have been archived. They are no longer updated by the system, but remain accessible for review, reactivation, or deletion."
          mouseEnterDelay={1}
        >
          <span data-testid="insights-archived-status-option">Archived</span>
        </Tooltip>
      ),
      value: "Archived",
    },
  ];

  const navigate = useNavigate();
  const { buildUrl } = useScopeParams();

  const tabBarExtraContent = (
    <div className="flex gap-2.5 items-center">
      <DisplayModeToggle
        value={compactMode ? "compact" : "expanded"}
        onChange={(value) => setCompactMode(value === "compact")}
        disabled={isInitialState}
        testIdPrefix="insights"
      />
      <SegmentedToggle
        className="text-sm rounded-[4px]"
        options={statusOptions}
        value={isActive ? "Active" : "Archived"}
        onChange={(value) => {
          setIsActive(value === "Active");
        }}
        disabled={isInitialState}
        testId="insights-status-toggle"
      />
    </div>
  );

  return (
    <div className="px-4.5 pt-7.5 mb-6">
      {contextHolder}
      <div className="flex justify-between items-center mb-[24px]">
        <div className="flex items-center gap-2">
          <h1 className="text-[18px] font-bold">Insights</h1>
          <span data-testid="insights-page-tooltip">
            <InfoTooltip title="Insights give you persistent, explainable, and refreshable visibility into your trial data. Create Insights from your AskVivo questions and see them collected and updated here." />
          </span>
        </div>
        <div className="flex gap-2.5 items-center">
          <Tooltip title="Ask Vivo a question to create a new insight. You'll be able to save it here for ongoing monitoring.">
            <Button
              type="primary"
              className="px-2"
              onClick={() => navigate(buildUrl("askvivo"))}
            >
              <AddIcon className="w-5 h-5" />
              <span>Create new insight</span>
            </Button>
          </Tooltip>
          <GuideButton pageName="/wiki/guides/insights-enhanced" />
        </div>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        renderTabBar={renderTabBar}
        tabBarExtraContent={tabBarExtraContent}
        items={categories.map((category) => ({
          label: category.label,
          key: category.key,
          children: (
            <InsightsPanelLayout
              mineInsights={getMineInsightsForCategory(category.key)}
              sharedInsights={getSharedInsightsForCategory(category.key)}
              isActive={isActive}
              compactMode={compactMode}
              onOpenDrawer={handleOpenDrawer}
              onOpenEditModal={handleOpenEditModal}
              onOpenDeleteModal={handleOpenDeleteModal}
              onOpenDeleteAlertModal={handleOpenDeleteAlertModal}
              onArchiveInsight={handleArchiveInsight}
              onActivateInsight={handleActivateInsight}
              onOpenAddAlertModal={handleOpenAlertModal}
              onOpenShareModal={handleOpenShareModal}
            />
          ),
          disabled: isInitialState,
        }))}
      />
      <InsightInformationDrawer
        insight={currentInsight as InsightProps}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onEdit={handleOpenEditModal}
        onArchive={handleArchiveInsight}
        onActivate={handleActivateInsight}
      />
      <InsightModal
        insight={currentInsight as InsightProps}
        isOpen={activeModal === "edit"}
        onSubmit={handleEditInsightWithModal}
        onClose={() => setActiveModal(null)}
        mode="edit"
        confirmLoading={confirmSubmitLoading}
      />
      <DeleteInsightModal
        insight={currentInsight as any}
        isOpen={activeModal === "delete"}
        onClose={() => setActiveModal(null)}
        confirmLoading={confirmDeleteLoading}
        handleOK={handleDeleteWithModal as any}
        mode={deleteMode}
      />
      <AddAlertModal
        insight={currentInsight as InsightProps}
        isOpen={activeModal === "alert"}
        onClose={() => setActiveModal(null)}
      />
      <ShareInsightModal
        insight={currentInsight as InsightProps}
        isOpen={activeModal === "share"}
        onClose={() => setActiveModal(null)}
        onShare={handleShareInsight}
        onStopSharing={handleStopSharing}
        isLoading={confirmSubmitLoading}
      />
    </div>
  );
}
